import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
} from '@angular/core';
import {
  NgbDate,
  NgbCalendar,
  NgbInputDatepicker,
} from '@ng-bootstrap/ng-bootstrap';
import { ChartConfiguration, ChartOptions, ChartType } from 'chart.js';
import * as L from 'leaflet';

@Component({
  selector: 'app-rtl',
  templateUrl: './rtl.component.html',
  styleUrls: ['./rtl.component.scss'],
})
export class RtlComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('map2', {static: true}) private map2ElementRef!: ElementRef<HTMLElement>;
  @ViewChild('d', {static: false}) datepicker!: NgbInputDatepicker;

  private map2?: L.Map;

  // ===================== Performance Indicator (BAR) =====================
  readonly performanceIndicatorBarchartType: ChartType = 'bar';

  readonly performanceIndicatorBarchartData: ChartConfiguration<'bar'>['data'] = {
    labels: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    datasets: [
      {
        data: [30, 25, 50, 25, 50, 25, 50, 55, 20, 35, 25, 30],
        label: 'Complaints',
        backgroundColor: '#0090e7',
      },
      {
        data: [25, 50, 10, 35, 30, 15, 20, 20, 30, 25, 10, 15],
        label: 'Task Done',
        backgroundColor: '#00d25b',
      },
      {
        data: [45, 25, 40, 40, 20, 60, 30, 25, 50, 40, 65, 55],
        label: 'Attends',
        backgroundColor: '#ff4d6b',
      },
    ],
  };

  readonly performanceIndicatorBarchartOptions: ChartOptions<'bar'> = {
    responsive: true, // keep as false if your layout depends on fixed size
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        max: 100,
        ticks: {
          stepSize: 25,
        },
        grid: {},
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false, // legend is handled by your card text
      },
    },
  };

  // ===================== Sessions by channel (DOUGHNUT) =====================
  readonly doughnutPieType: ChartType = 'doughnut';

  readonly centerTextPlugin = {
    id: 'centerText',
    beforeDatasetsDraw(chart: any) {
      const {ctx, chartArea} = chart;

      if (!chartArea) return;

      const centerX = (chartArea.left + chartArea.right) / 2;
      const centerY = (chartArea.top + chartArea.bottom) / 2;

      ctx.save();

      // Draw main text (8.234)
      ctx.font = 'bold 32px Arial';
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('8.234', centerX, centerY - 10);

      // Draw subtitle (Total Leads)
      ctx.font = '600 14px Arial';
      ctx.fillStyle = '#6c757d';
      ctx.fillText('Total Leads', centerX, centerY + 20);

      ctx.restore();
    }
  };

  readonly doughnutPieData: ChartConfiguration<'doughnut'>['data'] = {
    labels: ['Reassigned', 'Not Assigned', 'Assigned'],
    datasets: [
      {
        data: [55, 25, 20],
        backgroundColor: ['#ffca00', '#38ce3c', '#ff4d6b'],
        borderColor: ['#ffca00', '#38ce3c', '#ff4d6b'],
        borderWidth: 1,
      },
    ],
  };

  readonly doughnutPieOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    cutout: '75%',
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  plugins = [this.centerTextPlugin];

  // ===================== Date range picker props =====================
  hoveredDate: NgbDate | null = null;
  fromDate: NgbDate | null;
  toDate: NgbDate | null;
  onFirstSelection = true;

  // ===================== Income & Expenses (LINE) =====================
  readonly incomeExpenseSummaryChartType: ChartType = 'line';

  readonly incomeExpenseSummaryChartData: ChartConfiguration<'line'>['data'] = {
    labels: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    datasets: [
      {
        data: [505, 781, 480, 985, 410, 822, 388, 874, 350, 642, 320, 796],
        label: 'Total Income',
        fill: true,
        borderColor: '#6469df',
        backgroundColor: 'rgba(100, 105, 223, 0.1)',
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0,
      },
      {
        data: [700, 430, 725, 390, 686, 392, 757, 500, 820, 400, 962, 420],
        label: 'Total Expense',
        fill: true,
        borderColor: '#37ca32',
        backgroundColor: 'rgba(55, 202, 50, 0.1)',
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0,
      },
    ],
  };

  readonly incomeExpenseSummaryChartOptions: ChartOptions<'line'> = {
    responsive: true,
    elements: {
      line: {
        tension: 0.3,
      },
      point: {
        radius: 0,
      },
    },
    scales: {
      y: {
        min: 250,
        max: 1000,
        ticks: {
          stepSize: 250,
        },
        grid: {},
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  // ===================== Date range picker methods =====================
  onDateSelection(date: NgbDate): void {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
      this.onFirstSelection = false;
    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.toDate = date;
      this.onFirstSelection = true;
      this.datepicker.close();
    } else {
      this.toDate = null;
      this.fromDate = date;
      this.onFirstSelection = false;
    }
  }

  toNativeDate(date: NgbDate | null): Date | null {
    if (!date) {
      return null;
    }
    // month is 0-based in JS Date
    return new Date(date.year, date.month - 1, date.day);
  }

  isHovered(date: NgbDate): boolean {
    return (
      !!this.fromDate &&
      !this.toDate &&
      !!this.hoveredDate &&
      date.after(this.fromDate) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate): boolean {
    return (
      !!this.fromDate &&
      !!this.toDate &&
      date.after(this.fromDate) &&
      date.before(this.toDate)
    );
  }

  isRange(date: NgbDate): boolean {
    return (
      (!!this.fromDate && date.equals(this.fromDate)) ||
      (!!this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  constructor(private readonly calendar: NgbCalendar) {
    this.fromDate = this.calendar.getToday();
    this.toDate = this.calendar.getNext(this.calendar.getToday(), 'd', 10);
  }

  ngOnInit(): void {
    // other init logic if needed
  }

  ngAfterViewInit(): void {
    this.initializeMap2();
  }

  ngOnDestroy(): void {
    if (this.map2) {
      this.map2.remove();
    }
  }

  // ===================== Leaflet map =====================
  private initializeMap2(): void {
    if (this.map2) {
      return; // already initialized
    }

    this.map2 = L.map(this.map2ElementRef.nativeElement).setView(
      [51.505, -0.09],
      13
    );

    L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
      {
        maxZoom: 19,
        attribution: '© OpenStreetMap contributors, © CARTO',
      }
    ).addTo(this.map2);

    const customIcon = L.divIcon({
      className: 'custom-marker',
      html:
        '<div style="background: #ff4757; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);"></div>',
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });

    L.marker([51.5, -0.09], {icon: customIcon})
      .addTo(this.map2)
      .bindPopup('Dark theme marker!');
  }
}
